import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, SectionTitle } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Spinner } from '@/components/LoadingScreen';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ApiRequestError } from '@/services/api';
import { securityService, type ActiveSession, type Passkey } from '@/services/security.service';
import {
  hasPlatformAuthenticator,
  isWebAuthnSupported,
  passkeyMethodLabel,
  PasskeyCancelled,
} from '@/utils/webauthn';
import { v } from '@/theme/tokens';

/**
 * Настройки → Безопасность.
 *
 * Everything here is about the signed-in person's own account, and every
 * control does something real: revoking a session ends it server-side, not
 * just in this browser. Nothing technical about a passkey is shown — no
 * credential id, no public key — because none of it is anything a person can
 * act on, and the API does not return it in the first place.
 */

const HINT_KEY = 'ramenten-passkey-hint';

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const rowStyle = {
  display: 'flex',
  // Wraps rather than overflowing on a phone; the actions drop below the name.
  flexWrap: 'wrap' as const,
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  padding: '11px 0',
  borderBottom: `1px solid ${v('line-soft')}`,
};

const errorStyle = {
  color: v('negative'),
  fontSize: 12.5,
  marginBottom: 10,
};

const muted = { fontSize: 12, color: v('ink-muted') };

// --- passkeys ---------------------------------------------------------------

function PasskeyRow({
  passkey,
  onRename,
  onDelete,
  busy,
}: {
  passkey: Passkey;
  onRename: (name: string) => void;
  onDelete: () => void;
  busy: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(passkey.friendly_name);

  if (editing) {
    return (
      <div style={rowStyle}>
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          aria-label="Название passkey"
          maxLength={64}
          style={{
            flex: '1 1 180px',
            minWidth: 0,
            padding: '8px 10px',
            border: `1px solid ${v('line')}`,
            borderRadius: 8,
            fontSize: 13.5,
            fontFamily: 'inherit',
            background: v('surface'),
            color: v('ink'),
          }}
        />
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            variant="primary"
            disabled={busy || !draft.trim()}
            onClick={() => {
              onRename(draft.trim());
              setEditing(false);
            }}
          >
            Сохранить
          </Button>
          <Button
            onClick={() => {
              setDraft(passkey.friendly_name);
              setEditing(false);
            }}
          >
            Отмена
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={rowStyle}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, overflowWrap: 'anywhere' }}>
          {passkey.friendly_name}
        </div>
        <div style={muted}>
          Добавлен: {formatDate(passkey.created_at)}
          {' · '}
          Последний вход: {formatDate(passkey.last_used_at)}
        </div>
        <div style={muted}>
          {passkey.synced
            ? 'Синхронизируется с облаком устройства'
            : 'Хранится только на этом устройстве'}
        </div>
      </div>
      {/* Always-visible buttons, never hover-only: there is no hover on a phone. */}
      <div style={{ display: 'flex', gap: 8 }}>
        <Button onClick={() => setEditing(true)} disabled={busy}>
          Переименовать
        </Button>
        <Button variant="danger" onClick={onDelete} disabled={busy}>
          Удалить
        </Button>
      </div>
    </div>
  );
}

function PasskeysCard() {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Passkey | null>(null);
  const [supported, setSupported] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const platform = isWebAuthnSupported() ? await hasPlatformAuthenticator() : false;
      if (!cancelled) setSupported(platform);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const passkeys = useQuery({
    queryKey: ['security', 'passkeys'],
    queryFn: ({ signal }) => securityService.listPasskeys(signal),
  });

  const refresh = () => void client.invalidateQueries({ queryKey: ['security', 'passkeys'] });

  const fail = (fallback: string) => (e: unknown) => {
    if (e instanceof PasskeyCancelled) return; // dismissing the sheet is not a failure
    setError(e instanceof ApiRequestError ? e.message : fallback);
  };

  const add = useMutation({
    mutationFn: () => securityService.addPasskey(),
    onSuccess: () => {
      setError(null);
      window.sessionStorage.removeItem(HINT_KEY);
      refresh();
    },
    onError: fail('Не удалось добавить passkey'),
  });

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      securityService.renamePasskey(id, name),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail('Не удалось переименовать passkey'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => securityService.deletePasskey(id),
    onSuccess: () => {
      setError(null);
      setPendingDelete(null);
      refresh();
    },
    onError: fail('Не удалось удалить passkey'),
  });

  const items = passkeys.data ?? [];
  const busy = add.isPending || rename.isPending || remove.isPending;

  return (
    <Card>
      <SectionTitle
        right={items.length ? <Badge tone="positive">{items.length}</Badge> : undefined}
      >
        Passkeys
      </SectionTitle>

      <p style={{ ...muted, marginTop: 0 }}>
        Быстрый вход через {passkeyMethodLabel()}. Ключ создаётся и хранится в защищённом
        хранилище устройства — сервер получает только открытую часть и никогда не видит
        отпечаток или снимок лица. Пароль продолжает работать: passkey его не заменяет.
      </p>

      {supported === false && (
        <div
          style={{
            background: v('warning-soft'),
            color: v('warning'),
            borderRadius: 10,
            padding: '9px 11px',
            fontSize: 12.5,
            marginBottom: 10,
          }}
        >
          Этот браузер или устройство не поддерживает passkey. Вход по паролю доступен как
          обычно.
        </div>
      )}

      {error && (
        <div role="alert" style={errorStyle}>
          {error}
        </div>
      )}

      {passkeys.isPending && <Spinner />}

      {items.map((passkey) => (
        <PasskeyRow
          key={passkey.id}
          passkey={passkey}
          busy={busy}
          onRename={(name) => rename.mutate({ id: passkey.id, name })}
          onDelete={() => setPendingDelete(passkey)}
        />
      ))}

      {passkeys.isSuccess && items.length === 0 && (
        <div style={{ ...muted, padding: '10px 0' }}>
          Passkey ещё не добавлен.
        </div>
      )}

      <Button
        variant="primary"
        onClick={() => add.mutate()}
        disabled={busy || supported === false}
        style={{ marginTop: 14 }}
      >
        {add.isPending ? 'Подтвердите на устройстве…' : 'Добавить Passkey'}
      </Button>

      {pendingDelete && (
        <ConfirmDialog
          title="Удалить passkey?"
          message={`«${pendingDelete.friendly_name}» перестанет открывать вход. Войти по email и паролю можно будет как обычно.`}
          confirmLabel="Удалить"
          tone="danger"
          busy={remove.isPending}
          onConfirm={() => remove.mutate(pendingDelete.id)}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </Card>
  );
}

// --- sessions ---------------------------------------------------------------

function SessionsCard() {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [confirmOthers, setConfirmOthers] = useState(false);

  const sessions = useQuery({
    queryKey: ['security', 'sessions'],
    queryFn: ({ signal }) => securityService.listSessions(signal),
  });

  const refresh = () => void client.invalidateQueries({ queryKey: ['security', 'sessions'] });
  const fail = (fallback: string) => (e: unknown) =>
    setError(e instanceof ApiRequestError ? e.message : fallback);

  const revoke = useMutation({
    mutationFn: (id: string) => securityService.revokeSession(id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail('Не удалось завершить сессию'),
  });

  const revokeOthers = useMutation({
    mutationFn: () => securityService.revokeOtherSessions(),
    onSuccess: () => {
      setError(null);
      setConfirmOthers(false);
      refresh();
    },
    onError: fail('Не удалось завершить сессии'),
  });

  const items = sessions.data ?? [];
  const others = items.filter((session) => !session.current).length;

  return (
    <Card>
      <SectionTitle>Активные сессии</SectionTitle>

      <p style={{ ...muted, marginTop: 0 }}>
        Устройства, на которых выполнен вход. Завершение сессии действует немедленно — на том
        устройстве потребуется войти заново.
      </p>

      {error && (
        <div role="alert" style={errorStyle}>
          {error}
        </div>
      )}

      {sessions.isPending && <Spinner />}

      {items.map((session: ActiveSession) => (
        <div key={session.id} style={rowStyle}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, overflowWrap: 'anywhere' }}>
              {session.device_label}
              {session.current && (
                <span style={{ marginLeft: 8 }}>
                  <Badge tone="accent">Текущее устройство</Badge>
                </span>
              )}
            </div>
            <div style={muted}>
              Вход: {formatDateTime(session.created_at)}
              {' · '}
              Активность: {formatDateTime(session.last_seen_at)}
            </div>
            <div style={muted}>
              Способ входа: {session.auth_method === 'passkey' ? 'Passkey' : 'Пароль'}
            </div>
          </div>
          {!session.current && (
            <Button
              variant="danger"
              onClick={() => revoke.mutate(session.id)}
              disabled={revoke.isPending}
            >
              Завершить сессию
            </Button>
          )}
        </div>
      ))}

      <Button
        onClick={() => setConfirmOthers(true)}
        disabled={others === 0 || revokeOthers.isPending}
        style={{ marginTop: 14 }}
      >
        Выйти на всех других устройствах
        {others > 0 ? ` (${others})` : ''}
      </Button>

      {confirmOthers && (
        <ConfirmDialog
          title="Выйти на всех других устройствах?"
          message="Текущее устройство останется в системе. На остальных потребуется войти заново."
          confirmLabel="Выйти везде"
          tone="danger"
          busy={revokeOthers.isPending}
          onConfirm={() => revokeOthers.mutate()}
          onCancel={() => setConfirmOthers(false)}
        />
      )}
    </Card>
  );
}

// --- password ---------------------------------------------------------------

function PasswordCard() {
  const client = useQueryClient();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<number | null>(null);

  const change = useMutation({
    mutationFn: () => securityService.changePassword(current, next, confirm),
    onSuccess: (revoked) => {
      setError(null);
      setDone(revoked);
      setCurrent('');
      setNext('');
      setConfirm('');
      void client.invalidateQueries({ queryKey: ['security', 'sessions'] });
    },
    onError: (e: unknown) => {
      setDone(null);
      setError(
        e instanceof ApiRequestError
          ? e.message
          : 'Не удалось изменить пароль',
      );
    },
  });

  const field = {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${v('line')}`,
    borderRadius: 9,
    margin: '5px 0 10px',
    fontSize: 13.5,
    fontFamily: 'inherit',
    background: v('surface'),
    color: v('ink'),
  };

  const mismatch = confirm.length > 0 && next !== confirm;

  return (
    <Card>
      <SectionTitle>Пароль</SectionTitle>

      <p style={{ ...muted, marginTop: 0 }}>
        После смены пароля сессии на других устройствах завершаются. Passkeys остаются: они
        не связаны с паролем и живут в защищённом хранилище устройства.
      </p>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!mismatch) change.mutate();
        }}
      >
        <label htmlFor="current-password" style={{ fontSize: 12.5, color: v('ink-sub'), fontWeight: 600 }}>
          Текущий пароль
        </label>
        <input
          id="current-password"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
          style={field}
        />

        <label htmlFor="new-password" style={{ fontSize: 12.5, color: v('ink-sub'), fontWeight: 600 }}>
          Новый пароль
        </label>
        <input
          id="new-password"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(event) => setNext(event.target.value)}
          style={field}
        />

        <label htmlFor="confirm-password" style={{ fontSize: 12.5, color: v('ink-sub'), fontWeight: 600 }}>
          Повторите новый пароль
        </label>
        <input
          id="confirm-password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          style={{ ...field, borderColor: mismatch ? v('negative') : v('line') }}
        />

        {mismatch && <div style={errorStyle}>Пароли не совпадают</div>}
        {error && (
          <div role="alert" style={errorStyle}>
            {error}
          </div>
        )}
        {done !== null && (
          <div style={{ color: v('positive'), fontSize: 12.5, marginBottom: 10 }}>
            Пароль изменён.
            {done > 0 ? ` Завершено других сессий: ${done}.` : ' Других активных сессий не было.'}
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          disabled={change.isPending || !current || !next || mismatch}
        >
          {change.isPending ? 'Сохранение…' : 'Изменить пароль'}
        </Button>
      </form>
    </Card>
  );
}

// --- the one-time nudge -----------------------------------------------------

/**
 * Offered once per browser after a password login, and never again.
 *
 * The flag is set by the login page and cleared here whichever button is
 * pressed, so «Не сейчас» genuinely means not again — a prompt that reappears
 * every session is how people learn to dismiss prompts without reading them.
 */
function PasskeyInvitation() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (window.sessionStorage.getItem(HINT_KEY) !== 'pending') return;
      const [platform, passkeys] = await Promise.all([
        hasPlatformAuthenticator(),
        securityService.listPasskeys().catch(() => [] as Passkey[]),
      ]);
      // Nothing to offer somebody who already has one.
      if (!cancelled) setVisible(platform && passkeys.length === 0);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const dismiss = () => {
    window.sessionStorage.removeItem(HINT_KEY);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <Card style={{ borderColor: v('accent'), background: v('accent-soft') }}>
      <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 6 }}>
        Включить быстрый вход с {passkeyMethodLabel()}?
      </div>
      <p style={{ ...muted, marginTop: 0 }}>
        Вход одним касанием вместо пароля. Пароль остаётся на месте — passkey его дополняет.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button
          variant="primary"
          onClick={() => {
            dismiss();
            document.getElementById('security-passkeys')?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          Добавить Passkey
        </Button>
        <Button onClick={dismiss}>Не сейчас</Button>
      </div>
    </Card>
  );
}

export function SecuritySection() {
  return (
    <div style={{ display: 'grid', gap: 16 }} id="security-passkeys">
      <PasskeyInvitation />
      <PasskeysCard />
      <SessionsCard />
      <PasswordCard />
    </div>
  );
}
